import React, { useEffect } from 'react';
import {
	Divider,
	Form,
	Input,
	InputNumber,
	Modal,
	Select,
	Switch,
	notification,
} from 'antd';
import {
	IRackDetail,
	IRackListItem,
	useCreateRackMutation,
	useGenerateRackStructureMutation,
	useUpdateRackMutation,
} from '../../../../redux/api/inventory';
import {
	SLOT_TYPES,
	STORAGE_TYPES,
	VOLUME_TYPES,
} from '../../constants/inventory';

interface Props {
	open: boolean;
	buildingId?: string;
	rack?: IRackListItem | null;
	onClose: () => void;
	/**
	 * Called with the created/updated rack after a successful save. The parent
	 * uses it to select the rack (e.g. open the newly created rack). See PTB-5922.
	 */
	onSaved?: (rack: IRackDetail) => void;
}

const RackFormModal: React.FC<Props> = ({ open, buildingId, rack, onClose, onSaved }) => {
	const [form] = Form.useForm();
	const isEdit = !!rack;

	const [createRack, { isLoading: creating }] = useCreateRackMutation();
	const [updateRack, { isLoading: updating }] = useUpdateRackMutation();
	const [generateStructure, { isLoading: generating }] =
		useGenerateRackStructureMutation();

	const storageType = Form.useWatch('storage_type', form);
	const generate = Form.useWatch('generate', form);
	const isSlots = SLOT_TYPES.includes(storageType);
	const isBins = VOLUME_TYPES.includes(storageType);

	useEffect(() => {
		if (!open) return;
		if (rack) {
			form.setFieldsValue({
				rack_code: rack.rack_code,
				rack_name: rack.rack_name,
				storage_type: rack.storage_type,
				min_size: rack.min_size,
				max_size: rack.max_size,
				description: rack.description,
				reservations_enabled: rack.reservations_enabled !== false,
				generate: false,
			});
		} else {
			form.resetFields();
			form.setFieldsValue({
				generate: false,
				reservations_enabled: true,
				num_shelves: 4,
				locations_per_shelf: 6,
			});
		}
	}, [open, rack, form]);

	const handleOk = async () => {
		const values = await form.validateFields();
		const rackPayload = {
			rack_code: values.rack_code,
			rack_name: values.rack_name,
			storage_type: values.storage_type,
			min_size: values.min_size ?? null,
			max_size: values.max_size ?? null,
			description: values.description ?? '',
			reservations_enabled: values.reservations_enabled !== false,
		};

		try {
			let savedRack: IRackDetail;
			if (isEdit && rack) {
				savedRack = await updateRack({ id: rack.id, body: rackPayload }).unwrap();
				notification.success({ message: `Rack ${values.rack_code} updated` });
			} else if (values.generate) {
				savedRack = await generateStructure({
					rack: { ...rackPayload, building: buildingId as string },
					num_shelves: values.num_shelves,
					locations_per_shelf: values.locations_per_shelf,
					defaults: {
						max_weight: values.default_max_weight ?? null,
						max_volume: values.default_max_volume ?? null,
						max_width: values.default_max_width ?? null,
						max_height: values.default_max_height ?? null,
					},
				}).unwrap();
				notification.success({
					message: `Rack ${values.rack_code} created with ${values.num_shelves} shelves`,
				});
			} else {
				savedRack = await createRack({
					...rackPayload,
					building: buildingId as string,
				}).unwrap();
				notification.success({ message: `Rack ${values.rack_code} created` });
			}
			onSaved?.(savedRack);
			onClose();
		} catch (err: any) {
			notification.error({
				message: 'Could not save rack',
				description:
					err?.data?.detail ||
					err?.data?.non_field_errors?.[0] ||
					'Please review the form and try again.',
			});
		}
	};

	return (
		<Modal
			title={isEdit ? `Edit Rack ${rack?.rack_code}` : 'Add Rack'}
			open={open}
			onCancel={onClose}
			onOk={handleOk}
			confirmLoading={creating || updating || generating}
			okText={isEdit ? 'Save' : 'Create'}
			destroyOnClose
			width={560}
		>
			<Form form={form} layout="vertical">
				<Form.Item
					name="rack_code"
					label="Rack Code"
					rules={[{ required: true, message: 'Rack code is required' }]}
				>
					<Input maxLength={2} placeholder="e.g. M" />
				</Form.Item>
				<Form.Item
					name="rack_name"
					label="Rack Name"
					rules={[{ required: true, message: 'Rack name is required' }]}
				>
					<Input placeholder="e.g. Medium Rack" />
				</Form.Item>
				<Form.Item
					name="storage_type"
					label="Storage Type"
					rules={[{ required: true, message: 'Storage type is required' }]}
				>
					<Select options={STORAGE_TYPES} placeholder="Select storage type" />
				</Form.Item>
				<Form.Item name="min_size" label="Min Size (in)">
					<InputNumber style={{ width: '100%' }} min={0} step={0.25} />
				</Form.Item>
				<Form.Item name="max_size" label="Max Size (in)">
					<InputNumber style={{ width: '100%' }} min={0} step={0.25} />
				</Form.Item>
				<Form.Item name="description" label="Description">
					<Input.TextArea rows={2} />
				</Form.Item>
				<Form.Item
					name="reservations_enabled"
					label="Allow reservations"
					valuePropName="checked"
					extra="Turn off to keep material on this rack out of CAM and shop-floor reservation until it is audited."
				>
					<Switch />
				</Form.Item>

				{!isEdit && (
					<>
						<Divider />
						<Form.Item
							name="generate"
							label="Generate structure (shelves + locations)"
							valuePropName="checked"
						>
							<Switch />
						</Form.Item>

						{generate && (
							<>
								<Form.Item
									name="num_shelves"
									label="Number of shelves"
									rules={[{ required: true }]}
								>
									<InputNumber style={{ width: '100%' }} min={1} max={50} />
								</Form.Item>
								<Form.Item
									name="locations_per_shelf"
									label="Locations per shelf"
									rules={[{ required: true }]}
								>
									<InputNumber style={{ width: '100%' }} min={1} max={50} />
								</Form.Item>
								<Form.Item name="default_max_weight" label="Default shelf max weight (lb)">
									<InputNumber style={{ width: '100%' }} min={0} placeholder="800" />
								</Form.Item>
								{isBins && (
									<Form.Item name="default_max_volume" label="Default bin volume (in³)">
										<InputNumber style={{ width: '100%' }} min={0} placeholder="675" />
									</Form.Item>
								)}
								{isSlots && (
									<>
										<Form.Item name="default_max_width" label="Default slot width (in)">
											<InputNumber style={{ width: '100%' }} min={0} step={0.25} />
										</Form.Item>
										<Form.Item name="default_max_height" label="Default slot height (in)">
											<InputNumber style={{ width: '100%' }} min={0} step={0.25} />
										</Form.Item>
									</>
								)}
							</>
						)}
					</>
				)}
			</Form>
		</Modal>
	);
};

export default RackFormModal;
